<template>
    <ModalBase v-if="visible" @close="close" :withContent="true">
        <h1>{{ trads.label }}</h1>
        <div>{{ trads.subhead.interpolate({ width: data.width, height: data.height }) }}</div>
        <section>
            <Spinner v-if="loading" :style="{ position: 'absolute' }"></Spinner>
            <h4>{{ trads.pickCaddyLabel }}</h4>
            <select required v-model="caddy">
                <option :value="undefined">––––</option>
                <option v-for="(caddy, i) in allCaddies || []" :key="i" :value="caddy">
                    {{
                        trads.caddyOption.interpolate({
                            name: caddy.terms.name[$locale] && caddy.terms.name[$locale].value,
                            width: caddy.config.width,
                            height: caddy.config.height
                        })
                    }}
                </option>
            </select>
            <fragment v-if="caddy">
                <div>
                    <label>{{ trads.horizontalCount }}</label>
                    <NumberInput v-model="horizontalCount"></NumberInput>
                </div>
                <div>
                    <label>{{ trads.verticalCount }}</label>
                    <NumberInput v-model="verticalCount"></NumberInput>
                </div>
                <h4>{{ trads.spacingLabel }}</h4>
                <label>{{ trads.horizontalSpacing }}</label>
                <NumberInput :disabled="horizontalCount < 2" v-model="horizontalSpacing"></NumberInput>
                <label>{{ trads.verticalSpacing }}</label>
                <NumberInput :disabled="verticalCount < 2" v-model="verticalSpacing"></NumberInput>
                <h4>{{ trads.paddingLabel }}</h4>
                <label>{{ trads.leftPadding }}</label>
                <NumberInput v-model="padding.left"></NumberInput>
                <label>{{ trads.rightPadding }}</label>
                <NumberInput v-model="padding.right"></NumberInput>
                <label>{{ trads.topPadding }}</label>
                <NumberInput v-model="padding.top"></NumberInput>
                <label>{{ trads.bottomPadding }}</label>
                <NumberInput v-model="padding.bottom"></NumberInput>

                <footer>
                    <RoundedButton :disabled="!canSave" @click="validate">{{ trads.save }}</RoundedButton>
                    <RoundedButton @click="close">{{ trads.cancel }}</RoundedButton>
                </footer>
            </fragment>
        </section>
    </ModalBase>
</template>

<script>
import ModalBase from "@/components/modal/ModalBase";
import TryLoad from "@/mixins/TryLoadMixin";
import componentService, { COMPONENT_TYPE, PLANO_STEP } from "@/services/component";
import RoundedButton from "@/catalog/components/rounded-button";
import NumberInput from "@/plano/components/number-input";
import Spinner from "@/components/common/Spinner";
import { Context } from "plano_engine";
import { positiveHandler } from "@/plano/utils/fixing";

export default {
    props: ["zoneId", "brandId", "campaignId", "zones"],
    components: { ModalBase, RoundedButton, NumberInput, Spinner },
    mixins: [TryLoad],
    data: () => ({
        visible: false,
        allCaddies: undefined,
        caddy: undefined,
        verticalCount: 0,
        horizontalCount: 0,
        horizontalSpacing: 0,
        verticalSpacing: 0,
        padding: {
            left: 0,
            right: 0,
            bottom: 0,
            top: 0
        }
    }),
    mounted() {
        window.generateFixings = this.open.bind(this);
    },
    watch: {
        zones() {
            this.allCaddies = undefined;
        },
        zoneId() {
            this.allCaddies = undefined;
        },
        verticalCount() {
            this.horizontalSpacing = this.verticalSpacing = 0;
            this.refreshPadding();
        },
        horizontalCount() {
            this.horizontalSpacing = this.verticalSpacing = 0;
            this.refreshPadding();
        },
        verticalSpacing() {
            this.refreshPadding();
        },
        horizontalSpacing() {
            this.refreshPadding();
        },
        caddy(value) {
            if (value) {
                const caddyWidth = value.config.width,
                    caddyHeight = value.config.height;

                this.horizontalCount = Math.floor(this.data.width / caddyWidth);
                this.verticalCount = Math.floor(this.data.height / caddyHeight);

                this.horizontalSpacing = this.verticalSpacing = 0;

                this.refreshPadding();
            }
        }
    },
    methods: {
        changeHorizontalCount: positiveHandler(function(e) {
            this.horizontalCount = e;
        }),
        changeVerticalCount: positiveHandler(function(e) {
            this.verticalCount = e;
        }),
        changeHorizontalSpacing: positiveHandler(function(e) {
            this.horizontalSpacing = e;
        }),
        changeVerticalSpacing: positiveHandler(function(e) {
            this.verticalSpacing = e;
        }),
        changeLeftPadding: positiveHandler(function(e) {
            this.padding.left = e;
        }),
        changeRightPadding: positiveHandler(function(e) {
            this.padding.right = e;
        }),
        changeTopPadding: positiveHandler(function(e) {
            this.padding.top = e;
        }),
        changeBottomPadding: positiveHandler(function(e) {
            this.padding.bottom = e;
        }),
        refreshPadding() {
            this.padding.left = this.padding.right = this.remainingWidth / 2;
            this.padding.top = this.padding.bottom = this.remainingHeight / 2;
        },
        open(data, onValidate, onCancel) {
            this.visible = true;
            this.data = {
                ...data,
                onValidate,
                onCancel
            };
            if (!this.allCaddies) this.fetchCaddies();
        },
        async fetchCaddies() {
            const caddies = await this.tryLoad(() =>
                componentService.byType(COMPONENT_TYPE.caddy, {
                    brandId: this.brandId,
                    campaignId: this.campaignId,
                    zoneId: this.zoneId,
                    zones: this.zones,
                    step: PLANO_STEP.template
                })
            );
            this.allCaddies = caddies.filter(s => {
                return s && s.config && typeof s.config.width === "number" && typeof s.config.height === "number";
            });
        },
        validate() {
            Context.memento.open();
            const incrementX = (this.caddy.config.width + this.horizontalSpacing) * Context.rescaleFactor;
            const incrementY = (this.caddy.config.height + this.verticalSpacing) * Context.rescaleFactor;

            for (let i = 0; i < this.horizontalCount; i++) {
                for (let j = 0; j < this.verticalCount; j++) {
                    const x = this.padding.left + this.caddy.config.width * i + this.horizontalSpacing * i + this.caddy.config.width / 2;
                    const y = this.padding.bottom + this.caddy.config.height * j + this.verticalSpacing * j + this.caddy.config.height / 2;

                    const localX = (-this.data.width / 2 + x) * Context.rescaleFactor;
                    const localY = (-this.data.height / 2 + y) * Context.rescaleFactor;

                    for (const anchor of this.caddy.config.clipDevices) {
                        this.data.element.add(
                            BoardFixing.create().position(localX + anchor.x * Context.rescaleFactor, localY + anchor.y * Context.rescaleFactor)
                        );
                    }
                }
            }

            this.close();
        },
        close() {
            this.visible = false;
            this.data && this.data.onCancel();
            this.data = undefined;
            this.caddy = undefined;
        }
    },
    computed: {
        remainingWidth() {
            return this.data.width - this.caddy.config.width * this.horizontalCount - this.horizontalSpacing * (this.horizontalCount - 1);
        },
        remainingHeight() {
            return this.data.height - this.caddy.config.height * this.verticalCount - this.verticalSpacing * (this.verticalCount - 1);
        },
        canSave() {
            return (
                this.caddy &&
                this.verticalCount > 0 &&
                this.horizontalCount > 0 &&
                this.verticalSpacing >= 0 &&
                this.horizontalSpacing >= 0 &&
                this.padding.left >= 0 &&
                this.padding.right >= 0 &&
                this.padding.bottom >= 0 &&
                this.padding.top >= 0 &&
                this.caddy.config.width * this.horizontalCount + Math.max(0, this.horizontalSpacing * (this.horizontalCount - 1)) <=
                    this.data.width &&
                this.caddy.config.height * this.verticalCount + Math.max(0, this.verticalSpacing * (this.verticalCount - 1)) <= this.data.height
            );
        },
        trads() {
            return this.$trad.plano.editor.generateFixings;
        }
    },
    beforeDestroy() {
        window.generateFixings = undefined;
        this.close();
    }
};
</script>

<style lang="scss" scoped>
select {
    display: block;
}

input {
    width: 75px;
}

section {
    display: grid;
    justify-content: start;
    align-content: start;
    justify-items: start;
    align-items: center;
    padding: 15px 5px 0 5px;
    column-gap: 10px;
    grid-row-gap: 3px;
    position: relative;

    grid-template:
        "a a a a" "b b b b" "c c d d" "e e e e" "f g g g" "h i i i" "j j j j" "k l m n" "o p q r" "footer footer footer footer"
        / 1fr 1fr 1fr 1fr;

    > h4 {
        margin-top: 15px !important;
    }

    footer {
        grid-area: footer;
        width: 100%;
        margin-top: 10px;
    }

    > :nth-child(1) {
        grid-area: a;
    }
    > :nth-child(2) {
        grid-area: b;
    }
    > :nth-child(3) {
        grid-area: c;
    }
    > :nth-child(4) {
        grid-area: d;
    }
    > :nth-child(5) {
        grid-area: e;
    }
    > :nth-child(6) {
        grid-area: f;
    }
    > :nth-child(7) {
        grid-area: g;
    }
    > :nth-child(8) {
        grid-area: h;
    }
    > :nth-child(9) {
        grid-area: i;
    }
    > :nth-child(10) {
        grid-area: j;
    }
    > :nth-child(11) {
        grid-area: k;
    }
    > :nth-child(12) {
        grid-area: l;
    }
    > :nth-child(13) {
        grid-area: m;
    }
    > :nth-child(14) {
        grid-area: n;
    }
    > :nth-child(15) {
        grid-area: o;
    }
    > :nth-child(16) {
        grid-area: p;
    }
    > :nth-child(17) {
        grid-area: q;
    }
    > :nth-child(18) {
        grid-area: r;
    }
}
</style>



// WEBPACK FOOTER //
// index.vue?5d9a5b91